/**
 * Request Details Page
 * Shows full details of a request with helper applications and integrated chat
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { requestsAPI, chatAPI, ratingsAPI, usersAPI, helpersAPI } from '../../services/api';
import * as socketService from '../../services/socket';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import {
  ArrowLeft,
  MapPin,
  Calendar,
  Clock,
  Ship,
  Users,
  Star,
  MessageCircle,
  CheckCircle,
  XCircle,
  AlertCircle,
  Send,
  ChevronDown,
  ChevronUp,
  Award,
  Globe,
  Briefcase,
  Info,
  X,
  UserCheck,
  Edit2,
  FileText,
  Download,
  FileEdit,
  ArrowRight,
} from 'lucide-react';
import { format } from 'date-fns';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const UPLOADS_BASE_URL = API_BASE_URL.replace(/\/api\/?$/, '');

function RequestDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useNotifications();
  const { user } = useAuth();

  const [request, setRequest] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Chat state (for selected helper only)
  const [helperChatData, setHelperChatData] = useState(null);
  const [helperMessages, setHelperMessages] = useState([]);
  const [helperChatLoading, setHelperChatLoading] = useState(false);
  const [helperNewMessage, setHelperNewMessage] = useState('');
  const [helperSendingMessage, setHelperSendingMessage] = useState(false);
  const [helperUnreadCount, setHelperUnreadCount] = useState(0);
  const helperMessagesEndRef = useRef(null);

  // Rating state
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [rating, setRating] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);

  // Documents state
  const [expandedDocs, setExpandedDocs] = useState(null);
  const [docsData, setDocsData] = useState({});
  const [loadingDocs, setLoadingDocs] = useState(null);

  // More Info modal state
  const [showMoreInfo, setShowMoreInfo] = useState(null);
  const [moreInfoData, setMoreInfoData] = useState(null);
  const [loadingMoreInfo, setLoadingMoreInfo] = useState(false);

  useEffect(() => {
    loadRequestDetails();
  }, [id]);

  // Socket listener for real-time chat updates
  useEffect(() => {
    if (!helperChatData?.id) return;

    socketService.joinConversation(helperChatData.id);

    const unsubMessage = socketService.on(socketService.EVENTS.NEW_MESSAGE, (data) => {
      if (data?.conversation_id === helperChatData.id && data?.message) {
        setHelperMessages(prev => {
          // Avoid duplicates
          if (prev.some(m => m.id === data.message.id)) return prev;
          return [...prev, data.message];
        });
      }
    });

    return () => {
      socketService.leaveConversation(helperChatData.id);
      unsubMessage();
    };
  }, [helperChatData?.id]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (helperMessages.length > 0) {
      setTimeout(() => helperMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, [helperMessages]);

  const loadRequestDetails = async () => {
    try {
      const [requestData, applicationsData] = await Promise.all([
        requestsAPI.getById(id),
        requestsAPI.getApplications(id),
      ]);
      // API returns { success: true, data: {...} }
      const reqData = requestData.data || requestData.request;
      setRequest(reqData);
      setApplications(applicationsData.data || applicationsData.applications || []);

      // Load chat if a helper has been selected
      if (reqData?.helper?.id) {
        loadHelperChat(reqData.helper.id);
      }
    } catch (error) {
      console.error('Failed to load request:', error);
      showToast('Failed to load request details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadHelperChat = async (helperId) => {
    setHelperChatLoading(true);
    try {
      const response = await chatAPI.getRequestConversation(id, helperId);
      setHelperChatData(response.data);
      const msgs = response.data.messages || [];
      setHelperMessages(msgs);
      const unread = msgs.filter(m => m.sender_id !== user?.id && !(m.read_by || []).includes(user?.id)).length;
      setHelperUnreadCount(unread);
      if (response.data?.id && unread > 0) {
        chatAPI.markAsRead(response.data.id).then(() => setHelperUnreadCount(0));
      }
      // Scroll to bottom after loading messages
      setTimeout(() => helperMessagesEndRef.current?.scrollIntoView({ behavior: 'auto' }), 150);
    } catch (error) {
      console.error('Failed to load helper chat:', error);
    } finally {
      setHelperChatLoading(false);
    }
  };

  const handleSendHelperMessage = async () => {
    if (!helperNewMessage.trim() || helperSendingMessage || !helperChatData) return;

    setHelperSendingMessage(true);
    const messageContent = helperNewMessage.trim();
    setHelperNewMessage('');

    try {
      const response = await chatAPI.sendMessage(helperChatData.id, messageContent);
      setHelperMessages(prev => [...prev, response.data]);
      setTimeout(() => helperMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (error) {
      setHelperNewMessage(messageContent);
      showToast('Failed to send message', 'error');
    } finally {
      setHelperSendingMessage(false);
    }
  };

  const handleSelectHelper = async (helperId) => {
    setActionLoading(true);
    try {
      await requestsAPI.selectHelper(id, helperId);
      showToast('Helper selected successfully!', 'success');
      loadRequestDetails();
    } catch (error) {
      showToast(error.message || 'Failed to select helper', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this request?')) return;

    setActionLoading(true);
    try {
      await requestsAPI.cancel(id);
      showToast('Request cancelled', 'success');
      navigate('/owner/requests');
    } catch (error) {
      showToast(error.message || 'Failed to cancel request', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkComplete = async () => {
    // Show rating modal first - combine mark complete with rating
    setShowRatingModal(true);
  };

  const handleCompleteAndRate = async () => {
    if (rating === 0) {
      showToast('Please select a rating', 'error');
      return;
    }
    setSubmittingRating(true);
    try {
      const response = await requestsAPI.completeAndRate(id, rating, ratingComment);
      if (response.data?.fully_completed) {
        showToast('Job completed! Thank you for your rating.', 'success');
      } else {
        showToast('Marked as complete with rating. Waiting for helper to confirm.', 'success');
      }
      setShowRatingModal(false);
      setRating(0);
      setRatingComment('');
      loadRequestDetails();
    } catch (error) {
      showToast(error.message || 'Failed to complete', 'error');
    } finally {
      setSubmittingRating(false);
    }
  };

  const handleMarkFullyStaffed = async () => {
    if (!window.confirm("Mark this request as fully staffed? This will close it to new applications.")) return;
    setActionLoading(true);
    try {
      await requestsAPI.markFullyStaffed(id);
      showToast("Request marked as fully staffed!", "success");
      loadRequestDetails();
    } catch (error) {
      showToast(error.message || "Failed to mark as fully staffed", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handlePositionFilled = async () => {
    if (!window.confirm("Close this position? This will reject any pending applications and mark the position as filled.")) return;
    setActionLoading(true);
    try {
      await requestsAPI.closePosition(id);
      showToast("Position marked as filled!", "success");
      loadRequestDetails();
    } catch (error) {
      showToast(error.message || "Failed to close position", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmitRating = async () => {
    if (rating === 0) {
      showToast('Please select a rating', 'error');
      return;
    }
    setSubmittingRating(true);
    try {
      await ratingsAPI.submit(id, rating, ratingComment);
      showToast('Thank you for your rating!', 'success');
      setShowRatingModal(false);
      loadRequestDetails();
    } catch (error) {
      showToast(error.message || 'Failed to submit rating', 'error');
    } finally {
      setSubmittingRating(false);
    }
  };

  const handleShowMoreInfo = async (userId) => {
    setShowMoreInfo(userId);
    setLoadingMoreInfo(true);
    try {
      const response = await usersAPI.getPublicProfile(userId);
      setMoreInfoData(response.data);
    } catch (error) {
      showToast('Failed to load profile', 'error');
      setShowMoreInfo(null);
    } finally {
      setLoadingMoreInfo(false);
    }
  };

  const handleToggleDocs = async (helperId) => {
    if (expandedDocs === helperId) {
      setExpandedDocs(null);
      return;
    }

    setExpandedDocs(helperId);

    if (!docsData[helperId]) {
      setLoadingDocs(helperId);
      try {
        const response = await helpersAPI.getHelperDocuments(helperId);
        setDocsData(prev => ({
          ...prev,
          [helperId]: response.data || []
        }));
      } catch (error) {
        console.error('Failed to load documents:', error);
        showToast('Failed to load documents', 'error');
        setDocsData(prev => ({
          ...prev,
          [helperId]: []
        }));
      } finally {
        setLoadingDocs(null);
      }
    }
  };

  const formatDocType = (type) => {
    const labels = {
      stcw95: 'STCW 95',
      certification: 'Certification',
      reference: 'Reference',
      recommendation: 'Recommendation',
      id_document: 'ID Document',
      cv: 'CV',
      other: 'Other',
    };
    return labels[type] || type;
  };

  const getStatusInfo = (status) => {
    const info = {
      pending: { color: 'yellow', label: 'Pending', icon: Clock },
      active: { color: 'yellow', label: 'Pending', icon: Clock },
      matched: { color: 'green', label: 'Matched', icon: CheckCircle },
      confirmed: { color: 'green', label: 'Confirmed', icon: CheckCircle },
      in_progress: { color: 'blue', label: 'In Progress', icon: Ship },
      completed: { color: 'gray', label: 'Completed', icon: CheckCircle },
      cancelled: { color: 'red', label: 'Cancelled', icon: XCircle },
    };
    return info[status] || info.pending;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  if (!request) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
        <h2 className="text-xl font-medium text-navy-900">Request not found</h2>
        <Link to="/owner/requests" className="btn-primary mt-4 inline-block">
          Back to Requests
        </Link>
      </div>
    );
  }

  const statusInfo = getStatusInfo(request.status);
  const StatusIcon = statusInfo.icon;
  // Chat enabled when a helper is selected
  const chatEnabled = !!request.helper;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate('/owner/requests')}
          className="flex items-center text-navy-600 hover:text-navy-800 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Requests
        </button>

        <div className="flex items-start justify-between">
          <div>
            {/* Request type badge */}
            <div className="mb-2">
              {request.request_category === 'position' ? (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gold-100 text-gold-800">
                  <Briefcase className="h-4 w-4 mr-1.5" />
                  Crew Position
                </span>
              ) : (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                  <MapPin className="h-4 w-4 mr-1.5" />
                  Mooring Helper
                </span>
              )}
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-navy-900">
              {request.request_category === 'position' && request.crew_position
                ? request.crew_position.replace(/_/g, ' ').replace(/^(deck|interior|galley|eng)\s/i, '').replace(/\b\w/g, l => l.toUpperCase())
                : request.port_name}
            </h1>
            <div className="flex items-center mt-2 space-x-4 text-navy-600">
              {request.request_category === 'position' ? (
                <>
                  <div className="flex items-center">
                    <MapPin className="h-4 w-4 mr-1" />
                    {request.port_name}
                  </div>
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-1" />
                    {format(new Date(request.start_date || request.scheduled_date), 'MMM d')} - {request.end_date ? format(new Date(request.end_date), 'MMM d, yyyy') : 'Ongoing'}
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-1" />
                    {format(new Date(request.scheduled_date), 'EEEE, MMM d, yyyy')}
                  </div>
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    {request.scheduled_time}
                  </div>
                </>
              )}
            </div>
          </div>
          <div className={`flex items-center px-4 py-2 rounded-full bg-${statusInfo.color}-100 text-${statusInfo.color}-800`}>
            <StatusIcon className="h-4 w-4 mr-2" />
            <span className="font-medium">{statusInfo.label}</span>
          </div>
        </div>
      </div>

      <div className={chatEnabled ? "grid md:grid-cols-2 gap-6" : ""}>
      {/* Request Details Card */}
      <div className="card">
        <h2 className="text-lg font-semibold tracking-tight text-navy-900 mb-4">
          Request Details
        </h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="flex items-start">
            <Ship className="h-5 w-5 text-navy-400 mt-0.5 mr-3" />
            <div>
              <p className="text-sm text-navy-500">Yacht</p>
              <p className="font-medium text-navy-900">{request.yacht_name || 'Not specified'}</p>
              {request.yacht_length && (
                <p className="text-sm text-navy-600">{request.yacht_length}m</p>
              )}
            </div>
          </div>

          {/* Show Position details for position requests */}
          {request.request_category === 'position' && request.crew_position && (
            <div className="flex items-start">
              <Briefcase className="h-5 w-5 text-navy-400 mt-0.5 mr-3" />
              <div>
                <p className="text-sm text-navy-500">Position</p>
                <p className="font-medium text-navy-900 capitalize">
                  {request.crew_position?.replace(/_/g, ' ').replace(/^(deck|interior|galley|eng)\s/i, '')}
                </p>
                {request.crew_department && (
                  <p className="text-sm text-navy-600 capitalize">{request.crew_department} Department</p>
                )}
              </div>
            </div>
          )}

          {/* Show contract period for position requests */}
          {request.request_category === 'position' && (request.start_date || request.end_date) && (
            <div className="flex items-start">
              <Calendar className="h-5 w-5 text-navy-400 mt-0.5 mr-3" />
              <div>
                <p className="text-sm text-navy-500">Contract Period</p>
                <p className="font-medium text-navy-900">
                  {request.start_date && format(new Date(request.start_date), 'MMM d, yyyy')}
                  {request.start_date && request.end_date && ' - '}
                  {request.end_date && format(new Date(request.end_date), 'MMM d, yyyy')}
                </p>
              </div>
            </div>
          )}

          {/* Show salary for position requests */}
          {request.request_category === 'position' && request.salary && (
            <div className="flex items-start">
              <Award className="h-5 w-5 text-navy-400 mt-0.5 mr-3" />
              <div>
                <p className="text-sm text-navy-500">Monthly Salary</p>
                <p className="font-medium text-navy-900">
                  {request.salary_currency === 'EUR' ? '€' : '$'}{request.salary}
                </p>
              </div>
            </div>
          )}

          {/* Helpers - only show for mooring requests */}
          {request.request_category !== 'position' && (
            <div className="flex items-start">
              <Users className="h-5 w-5 text-navy-400 mt-0.5 mr-3" />
              <div>
                <p className="text-sm text-navy-500">Helpers</p>
                <p className="font-medium text-navy-900"><span className="text-green-600">{request.helpers_confirmed || 0}</span>/{request.helpers_needed} confirmed</p>
              </div>
            </div>
          )}

          {/* Mooring Type - only show for mooring requests */}
          {request.request_category !== 'position' && (
            <div className="flex items-start">
              <MapPin className="h-5 w-5 text-navy-400 mt-0.5 mr-3" />
              <div>
                <p className="text-sm text-navy-500">Mooring Type</p>
                <p className="font-medium text-navy-900 capitalize">
                  {request.mooring_type?.replace('_', ' ') || 'Not specified'}
                </p>
              </div>
            </div>
          )}

          {/* Contract Type for position requests */}
          {request.request_category === 'position' && (
            <div className="flex items-start">
              <Clock className="h-5 w-5 text-navy-400 mt-0.5 mr-3" />
              <div>
                <p className="text-sm text-navy-500">Contract Type</p>
                <p className="font-medium text-navy-900">
                  {request.contract_type === 'permanent' ? 'Full Time' :
                   request.contract_type === 'seasonal' ? 'Seasonal' :
                   request.contract_type === 'temporary' ? 'Daywork' :
                   request.contract_type === 'delivery' ? 'Delivery' :
                   'Not specified'}
                </p>
              </div>
            </div>
          )}

          {/* Request Type - only show for mooring requests */}
          {request.request_category !== 'position' && (
            <div className="flex items-start">
              <Clock className="h-5 w-5 text-navy-400 mt-0.5 mr-3" />
              <div>
                <p className="text-sm text-navy-500">Request Type</p>
                <p className="font-medium text-navy-900 capitalize">
                  {request.request_type?.replace('_', ' ')}
                </p>
              </div>
            </div>
          )}
        </div>

        {request.notes && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-sm text-navy-500 mb-1">Notes</p>
            <p className="text-navy-700">{request.notes}</p>
          </div>
        )}
      </div>

      {/* Chat Section - only available after helper selection */}
      {chatEnabled && (
      <div className="card flex flex-col" style={{ minHeight: '400px' }}>
        <h2 className="text-lg font-semibold tracking-tight text-navy-900 mb-4 flex items-center">
          <MessageCircle className="h-5 w-5 mr-2" />
          Chat with Helper
          {helperUnreadCount > 0 && (
            <span className="ml-2 h-2.5 w-2.5 bg-red-500 rounded-full" />
          )}
        </h2>

        {helperChatLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="spinner w-6 h-6" />
          </div>
        ) : !helperChatData ? (
          <div className="flex-1 flex items-center justify-center text-navy-400">
            <div className="text-center">
              <MessageCircle className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p>No messages yet</p>
              <p className="text-sm mt-1">Start chatting with your selected helper</p>
            </div>
          </div>
        ) : (
          <>
            <div className="chat-messages flex-1 space-y-3 mb-4 pr-1" style={{ maxHeight: '280px' }}>
              {helperMessages.length === 0 ? (
                <div className="text-center text-navy-400 py-8">
                  <MessageCircle className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p>No messages yet</p>
                </div>
              ) : (
                helperMessages.map((message) => {
                  if (message.message_type === 'system') {
                    let parsed = null;
                    try { parsed = JSON.parse(message.content); } catch (e) { /* not JSON */ }

                    if (parsed?.type === 'request_modified' && parsed.changes) {
                      const changeEntries = Object.values(parsed.changes);
                      return (
                        <div key={message.id} className="flex justify-center">
                          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 max-w-xs w-full">
                            <div className="flex items-center text-amber-700 font-medium text-sm mb-2">
                              <FileEdit className="h-4 w-4 mr-1.5" />
                              Request Modified
                            </div>
                            <div className="space-y-1">
                              {changeEntries.map((c, idx) => (
                                <div key={idx} className="text-xs text-navy-700">
                                  <span className="font-medium">{c.label}:</span>{' '}
                                  <span className="text-navy-500">{c.from || '(empty)'}</span>
                                  <ArrowRight className="inline h-3 w-3 mx-1 text-amber-500" />
                                  <span className="text-navy-900 font-medium">{c.to || '(empty)'}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div key={message.id} className="flex justify-center">
                        <div className="text-xs text-navy-400 bg-gray-100 rounded-full px-3 py-1">
                          {message.content}
                        </div>
                      </div>
                    );
                  }

                  const isOwn = message.sender_id === user?.id;
                  return (
                    <div key={message.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                      <div className="max-w-[75%]">
                        <div className={`chat-bubble ${isOwn ? 'sent' : 'received'}`}>
                          {message.content}
                        </div>
                        <p className={`text-xs text-navy-400 mt-1 ${isOwn ? 'text-right' : ''}`}>
                          {message.sent_at ? format(new Date(message.sent_at), 'MMM d, h:mm a') : ''}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={helperMessagesEndRef} />
            </div>

            <div className="pt-3 border-t border-gray-100">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={helperNewMessage}
                  onChange={(e) => setHelperNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendHelperMessage()}
                  placeholder="Type a message..."
                  className="input-field flex-1 py-2"
                  disabled={helperSendingMessage}
                />
                <button
                  onClick={handleSendHelperMessage}
                  disabled={!helperNewMessage.trim() || helperSendingMessage}
                  className="btn-primary py-2 px-4"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      )}
      </div>

      {/* Selected Helper (if confirmed) */}
      {request.status !== 'pending' && request.helper && (
        <div className="rounded-apple-lg overflow-hidden bg-white" style={{ border: '1px solid rgba(0,0,0,0.06)' }}>
          {/* Section Header */}
          <div className="px-5 py-3 bg-green-50" style={{ borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
            <h2 className="text-sm font-medium text-green-700 flex items-center">
              <CheckCircle className="h-4 w-4 mr-1.5" />
              Selected Helper
            </h2>
          </div>

          {/* Header: Photo + Name/Rating + Documents Button */}
          <div className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center flex-1 min-w-0">
              <div className="h-14 w-14 bg-navy-100 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                {request.helper.profile_photo_url ? (
                  <img
                    src={request.helper.profile_photo_url.startsWith('http') ? request.helper.profile_photo_url : `${UPLOADS_BASE_URL}${request.helper.profile_photo_url}`}
                    alt=""
                    className="h-14 w-14 rounded-full object-cover"
                  />
                ) : (
                  <span className="text-navy-600 font-medium text-lg">
                    {request.helper.first_name?.[0]}{request.helper.last_name?.[0]}
                  </span>
                )}
              </div>
              <div className="ml-4 min-w-0">
                <p className="font-medium text-navy-900 text-base">
                  {request.helper.first_name} {request.helper.last_name}
                </p>
                <div className="flex items-center text-sm text-navy-500 mt-0.5">
                  {(request.helper.average_rating || request.helper.rating) ? (
                    <>
                      <Star className="h-3.5 w-3.5 text-gold-500 fill-current mr-1" />
                      <span className="font-medium text-navy-700">{(request.helper.average_rating || request.helper.rating).toFixed(1)}</span>
                    </>
                  ) : (
                    <span className="text-navy-400">No rating</span>
                  )}
                  {request.helper.experience_years > 0 && (
                    <span className="ml-3 text-navy-400">{request.helper.experience_years} yrs exp</span>
                  )}
                </div>
                {(request.helper.phone || request.helper.whatsapp) && (
                  <div className="flex items-center text-sm text-navy-500 mt-1">
                    <span>{request.helper.phone || request.helper.whatsapp}</span>
                    {request.helper.whatsapp && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
                        <svg className="h-3 w-3 mr-1" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                        </svg>
                        I'm on WhatsApp
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Helper Applications (if pending, active, or matched) */}
      {(request.status === 'pending' || request.status === 'active' || request.status === 'matched') && (
        <div>
          <h2 className="text-lg font-semibold text-navy-900 mb-4 tracking-tight">
            Applicants ({applications.length})
          </h2>

          {applications.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-apple-lg" style={{ border: '1px solid rgba(0,0,0,0.06)' }}>
              <Users className="h-10 w-10 text-navy-300 mx-auto mb-3" />
              <p className="text-navy-500">No applications yet</p>
              <p className="text-sm text-navy-400 mt-1 font-light">
                Helpers in this area will see your request and apply
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {applications.map((app) => {
                return (
                  <div key={app.id} className={`rounded-apple-lg overflow-hidden bg-white ${app.status === 'selected' ? 'ring-2 ring-green-400' : ''}`}
                    style={{ border: '1px solid rgba(0,0,0,0.06)' }}
                  >
                    {/* Header: Photo + Name/Rating + Buttons */}
                    <div className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                      <div className="flex items-center flex-1 min-w-0">
                        <div className="h-14 w-14 bg-navy-100 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {app.profile_photo_url ? (
                            <img
                              src={app.profile_photo_url.startsWith('http') ? app.profile_photo_url : `${UPLOADS_BASE_URL}${app.profile_photo_url}`}
                              alt=""
                              className="h-14 w-14 rounded-full object-cover"
                            />
                          ) : (
                            <span className="text-navy-600 font-medium text-lg">
                              {app.first_name?.[0]}{app.last_name?.[0]}
                            </span>
                          )}
                        </div>
                        <div className="ml-4 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-navy-900 text-base">
                              {app.first_name} {app.last_name}
                            </p>
                            {/* DAYWORK: Show helper type badge only for daywork requests */}
                            {request.contract_type === 'temporary' && (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                app.is_greenie
                                  ? 'bg-teal-100 text-teal-800'
                                  : 'bg-indigo-100 text-indigo-800'
                              }`}>
                                {app.helper_type || (app.is_greenie ? 'Mooring Helper' : 'Experienced Crew')}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center text-sm text-navy-500 mt-0.5">
                            <Star className="h-3.5 w-3.5 text-gold-500 fill-current mr-1" />
                            <span className="font-medium text-navy-700">{app.average_rating ? app.average_rating.toFixed(1) : 'New'}</span>
                            {app.rating_count > 0 && <span className="ml-1 text-navy-400">({app.rating_count} review{app.rating_count !== 1 ? 's' : ''})</span>}
                          </div>
                          {/* Age and Nationality */}
                          <div className="flex items-center gap-3 mt-1.5 text-sm text-navy-600">
                            {app.date_of_birth && (
                              <span className="inline-flex items-center">
                                <span className="font-medium">{Math.floor((new Date() - new Date(app.date_of_birth)) / 31557600000)} yrs old</span>
                              </span>
                            )}
                            {app.nationality && (
                              <span className="inline-flex items-center px-2 py-0.5 bg-primary-50 text-primary-700 rounded text-xs font-medium">
                                {({
                                  'AF': 'Afghan', 'AL': 'Albanian', 'DZ': 'Algerian', 'AR': 'Argentine', 'AU': 'Australian',
                                  'AT': 'Austrian', 'BE': 'Belgian', 'BR': 'Brazilian', 'BG': 'Bulgarian', 'CA': 'Canadian',
                                  'CL': 'Chilean', 'CN': 'Chinese', 'CO': 'Colombian', 'HR': 'Croatian', 'CY': 'Cypriot',
                                  'CZ': 'Czech', 'DK': 'Danish', 'NL': 'Dutch', 'EG': 'Egyptian', 'EE': 'Estonian',
                                  'FI': 'Finnish', 'FR': 'French', 'DE': 'German', 'GR': 'Greek', 'HU': 'Hungarian',
                                  'IN': 'Indian', 'ID': 'Indonesian', 'IE': 'Irish', 'IL': 'Israeli', 'IT': 'Italian',
                                  'JP': 'Japanese', 'LV': 'Latvian', 'LT': 'Lithuanian', 'MY': 'Malaysian', 'MT': 'Maltese',
                                  'MX': 'Mexican', 'MC': 'Monegasque', 'ME': 'Montenegrin', 'MA': 'Moroccan', 'NZ': 'New Zealander',
                                  'NO': 'Norwegian', 'PH': 'Filipino', 'PL': 'Polish', 'PT': 'Portuguese', 'RO': 'Romanian',
                                  'RU': 'Russian', 'SA': 'Saudi', 'RS': 'Serbian', 'SG': 'Singaporean', 'SK': 'Slovak',
                                  'SI': 'Slovenian', 'ZA': 'South African', 'KR': 'South Korean', 'ES': 'Spanish', 'SE': 'Swedish',
                                  'CH': 'Swiss', 'TH': 'Thai', 'TR': 'Turkish', 'UA': 'Ukrainian', 'AE': 'Emirati',
                                  'GB': 'British', 'US': 'American', 'OTHER': 'Other'
                                })[app.nationality] || app.nationality}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 items-center">
                        <button
                          onClick={() => handleShowMoreInfo(app.user_id)}
                          className="text-sm text-primary-600 hover:text-primary-700 font-normal py-2 px-3.5 rounded-full border border-primary-600 transition-all duration-300 inline-flex items-center"
                          title="View full profile"
                        >
                          <Info className="h-3.5 w-3.5 mr-1.5" />
                          More Info
                        </button>
                        <button
                          onClick={() => handleToggleDocs(app.helper_id)}
                          className={`text-sm font-normal py-2 px-3.5 rounded-full border transition-all duration-300 inline-flex items-center ${expandedDocs === app.helper_id ? 'bg-primary-50 border-primary-600 text-primary-700' : 'border-navy-300 text-navy-600 hover:border-navy-400'}`}
                          title="View documents"
                        >
                          <FileText className="h-3.5 w-3.5 mr-1.5" />
                          Docs
                          {expandedDocs === app.helper_id ? <ChevronUp className="h-3.5 w-3.5 ml-1" /> : <ChevronDown className="h-3.5 w-3.5 ml-1" />}
                        </button>
                        {app.status === 'selected' ? (
                          <span className="inline-flex items-center text-green-700 font-medium bg-green-50 px-4 py-2 rounded-full text-sm">
                            <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                            Selected
                          </span>
                        ) : (
                          <button
                            onClick={() => handleSelectHelper(app.helper_id)}
                            disabled={actionLoading || request.status === 'matched'}
                            className="btn-primary py-2 px-5 text-sm disabled:opacity-50"
                          >
                            Select
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Stats Row */}
                    <div className="px-5 pb-4" style={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                      <div className="grid grid-cols-3 gap-3 pt-4">
                        <div className="bg-navy-50 rounded-apple py-3 px-2 text-center">
                          <p className="text-lg font-semibold text-navy-900">{app.total_jobs || 0}</p>
                          <p className="text-[11px] text-navy-500 uppercase tracking-wider mt-0.5">Jobs</p>
                        </div>
                        <div className="bg-navy-50 rounded-apple py-3 px-2 text-center">
                          <p className="text-lg font-semibold text-navy-900">{app.experience_years || 0}</p>
                          <p className="text-[11px] text-navy-500 uppercase tracking-wider mt-0.5">Yrs Exp</p>
                        </div>
                        <div className="bg-navy-50 rounded-apple py-3 px-2 text-center">
                          <p className="text-lg font-semibold text-navy-900">{app.rating_count || 0}</p>
                          <p className="text-[11px] text-navy-500 uppercase tracking-wider mt-0.5">Reviews</p>
                        </div>
                      </div>
                    </div>

                    {/* Languages */}
                    {app.languages && app.languages.length > 0 && (
                      <div className="px-5 pb-4 pt-4" style={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                        <p className="text-[11px] font-medium text-navy-400 uppercase tracking-wider mb-2">Languages</p>
                        <p className="text-sm text-navy-700">{app.languages.join(' \u2022 ')}</p>
                      </div>
                    )}

                    {/* Certifications */}
                    {(app.certifications_json && Object.keys(app.certifications_json).some(k => app.certifications_json[k])) && (
                      <div className="px-5 pb-4 pt-4" style={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                        <p className="text-[11px] font-medium text-navy-400 uppercase tracking-wider mb-2">Certifications</p>
                        <div className="flex flex-wrap gap-1.5">
                          {Object.entries(app.certifications_json || {})
                            .filter(([_, hasIt]) => hasIt)
                            .map(([certId]) => {
                              const certLabels = {
                                cert_stcw_bst: 'STCW BST',
                                cert_eng1: 'ENG1',
                                cert_adv_fire: 'Adv Fire Fighting',
                                cert_pscrb: 'PSCRB',
                                cert_medical_care: 'Medical Care',
                                cert_sso: 'SSO',
                                cert_ism_isps: 'ISM/ISPS',
                                cert_oow_3000: 'OOW 3000GT',
                                cert_oow_500: 'OOW 500GT',
                                cert_yachtmaster: 'Yachtmaster',
                                cert_powerboat: 'Powerboat L2',
                                cert_gmdss_goc: 'GMDSS GOC',
                                cert_gmdss_src: 'GMDSS SRC',
                                cert_radar: 'Radar',
                                cert_ecdis: 'ECDIS',
                                cert_brm: 'BRM',
                                cert_padi: 'PADI',
                                cert_jet_ski: 'Jet Ski',
                                cert_food_hygiene: 'Food Hygiene',
                                cert_wset: 'WSET',
                                cert_silver_service: 'Silver Service',
                                cert_eoow: 'EOOW',
                                cert_atv_y4: 'AEC Y4',
                              };
                              return (
                                <span key={certId} className="inline-flex items-center px-2.5 py-1 bg-green-50 text-green-700 text-xs rounded-full">
                                  <Award className="h-3 w-3 mr-1" />
                                  {certLabels[certId] || certId.replace('cert_', '').replace(/_/g, ' ')}
                                </span>
                              );
                            })}
                        </div>
                      </div>
                    )}

                    {/* Skills */}
                    {app.skills && app.skills.length > 0 && (
                      <div className="px-5 pb-4 pt-4" style={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                        <p className="text-[11px] font-medium text-navy-400 uppercase tracking-wider mb-2">Skills</p>
                        <div className="flex flex-wrap gap-1.5">
                          {app.skills.slice(0, 5).map((skill, idx) => (
                            <span key={idx} className="px-3 py-1 bg-navy-100 text-navy-700 text-xs rounded-full">
                              {skill}
                            </span>
                          ))}
                          {app.skills.length > 5 && (
                            <span className="px-3 py-1 text-navy-400 text-xs">+{app.skills.length - 5} more</span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Application Message */}
                    {app.cover_message && (
                      <div className="px-5 pb-5 pt-4" style={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                        <p className="text-[11px] font-medium text-navy-400 uppercase tracking-wider mb-2">Application Message</p>
                        <div className="bg-navy-50 rounded-apple p-3.5">
                          <p className="text-sm text-navy-700 italic leading-relaxed">"{app.cover_message}"</p>
                        </div>
                      </div>
                    )}

                    {/* Documents Panel */}
                    {expandedDocs === app.helper_id && (
                      <div className="bg-navy-50 p-5" style={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                        <p className="text-[11px] font-medium text-navy-400 uppercase tracking-wider mb-3">Documents</p>
                        {loadingDocs === app.helper_id ? (
                          <div className="flex items-center justify-center py-6">
                            <div className="spinner w-6 h-6" />
                          </div>
                        ) : (docsData[app.helper_id] || []).length === 0 ? (
                          <div className="text-center text-navy-400 py-6">
                            <FileText className="h-7 w-7 mx-auto mb-2 opacity-40" />
                            <p className="text-sm">No documents uploaded</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {docsData[app.helper_id].map((doc) => (
                              <div key={doc.id} className="flex items-center justify-between p-3 bg-white rounded-apple">
                                <div className="flex items-center min-w-0">
                                  <div className="h-9 w-9 bg-primary-50 rounded-apple flex items-center justify-center flex-shrink-0">
                                    <FileText className="h-4 w-4 text-primary-600" />
                                  </div>
                                  <div className="ml-3 min-w-0">
                                    <p className="text-sm font-medium text-navy-900 truncate">{doc.file_name}</p>
                                    <div className="flex items-center gap-2 mt-0.5">
                                      <span className="text-xs text-primary-600">{formatDocType(doc.document_type)}</span>
                                      <span className="text-xs text-navy-400">{format(new Date(doc.uploaded_at), 'MMM d, yyyy')}</span>
                                    </div>
                                  </div>
                                </div>
                                <a
                                  href={`${UPLOADS_BASE_URL}${doc.file_path}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-2 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-full transition-colors duration-300 ml-2 flex-shrink-0"
                                >
                                  <Download className="h-4 w-4" />
                                </a>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      {(request.status === 'pending' || request.status === 'active') && (
        <div className="flex justify-end space-x-4">
          <Link
            to={`/owner/requests/${id}/edit`}
            className="btn-secondary inline-flex items-center"
          >
            <Edit2 className="h-4 w-4 mr-2" />
            Modify Request
          </Link>
          <button
            onClick={handleCancel}
            disabled={actionLoading}
            className="text-red-600 hover:text-red-700 font-medium"
          >
            Cancel Request
          </button>
        </div>
      )}

      {/* Actions for matched requests */}
      {request.status === 'matched' && (
        <div className="flex justify-end space-x-4">
          <Link
            to={`/owner/requests/${id}/edit`}
            className="btn-secondary inline-flex items-center"
          >
            <Edit2 className="h-4 w-4 mr-2" />
            Modify Request
          </Link>
          <button
            onClick={handleCancel}
            disabled={actionLoading}
            className="text-red-600 hover:text-red-700 font-medium"
          >
            Cancel Request
          </button>
        </div>
      )}


      {/* Mark as Fully Staffed Section */}
      {(request.status === "active" || request.status === "matched") && 
       (request.helpers_confirmed || 0) > 0 && 
       (request.helpers_confirmed || 0) < request.helpers_needed && (
        <div className="card bg-amber-50 border border-amber-200 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-amber-900">Helper bringing a team?</h3>
              <p className="text-sm text-amber-700 mt-1">
                If your selected helper is bringing additional crew members, you can mark this request as fully staffed.
              </p>
            </div>
            <button
              onClick={handleMarkFullyStaffed}
              disabled={actionLoading}
              className="btn-primary bg-amber-600 hover:bg-amber-700"
            >
              Mark as Fully Staffed
            </button>
          </div>
        </div>
      )}
      {/* Position Filled Button for crew position requests */}
      {request.request_category === 'position' && request.status === 'active' && (
        <div className="card bg-purple-50 border border-purple-200 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-purple-900">Ready to close this position?</h3>
              <p className="text-sm text-purple-700 mt-1">
                Once you've found your crew member, mark this position as filled to close it to new applications.
              </p>
            </div>
            <button
              onClick={handlePositionFilled}
              disabled={actionLoading}
              className="btn-primary bg-purple-600 hover:bg-purple-700"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Position Filled
            </button>
          </div>
        </div>
      )}

      {/* Mark Complete Section for matched requests */}
      {request.status === 'matched' && (
        <div className="card bg-blue-50 border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-blue-900">Job Ready to Complete?</h3>
              <p className="text-sm text-blue-700 mt-1">
                {request.owner_completed
                  ? 'You marked this complete. Waiting for helper to confirm.'
                  : request.request_category === 'position'
                    ? 'Once the mission is complete, mark this job as done.'
                    : 'Once the mooring assistance is done, mark this job as complete.'}
              </p>
            </div>
            {!request.owner_completed && (
              <button
                onClick={handleMarkComplete}
                disabled={actionLoading}
                className="btn-primary"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Mark Complete
              </button>
            )}
            {request.owner_completed && (
              <span className="inline-flex items-center text-blue-600 font-medium">
                <CheckCircle className="h-5 w-5 mr-1" />
                Waiting for helper
              </span>
            )}
          </div>
        </div>
      )}

      {/* Completed Status */}
      {request.status === 'completed' && (
        <div className="card bg-green-50 border border-green-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <CheckCircle className="h-8 w-8 text-green-600 mr-3" />
              <div>
                <h3 className="font-semibold text-green-900">Job Completed!</h3>
                <p className="text-sm text-green-700">
                  {request.request_category === 'position'
                    ? 'This crew position mission has been completed.'
                    : 'This mooring assistance has been completed.'}
                </p>
              </div>
            </div>
            {request.owner_rated ? (
              <span className="inline-flex items-center text-green-600 font-medium">
                <Star className="h-5 w-5 mr-1 text-gold-500" />
                Rated
              </span>
            ) : (
              <button
                onClick={() => setShowRatingModal(true)}
                className="btn-primary"
              >
                <Star className="h-4 w-4 mr-2" />
                Rate Helper
              </button>
            )}
          </div>
          {request.received_rating && (
            <div className="mt-4 pt-4 border-t border-green-200">
              <div className="flex items-center gap-1 mb-1">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star key={s} className={`h-4 w-4 ${s <= request.received_rating.rating ? 'text-gold-500 fill-current' : 'text-gray-300'}`} />
                ))}
                <span className="text-sm text-green-800 ml-1">({request.received_rating.rating}.0)</span>
              </div>
              {request.received_rating.comment && (
                <p className="text-sm text-green-800 italic">"{request.received_rating.comment}"</p>
              )}
              <p className="text-xs text-green-600 mt-1">
                — {request.received_rating.first_name} {request.received_rating.last_name}, {format(new Date(request.received_rating.created_at), 'MMM d, yyyy')}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Rating Modal */}
      {showRatingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <h2 className="text-xl font-semibold tracking-tight text-navy-900 mb-4">
              {request.status === 'matched' ? 'Complete Job & Rate Helper' : 'Rate the Helper'}
            </h2>
            <p className="text-navy-600 mb-4">
              {request.status === 'matched'
                ? 'Please rate the helper to complete this job. Your rating will be visible to the helper only after they also rate you.'
                : 'How was your experience with the mooring assistance?'}
            </p>

            {/* Star Rating */}
            <div className="flex justify-center space-x-2 mb-6">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  className="focus:outline-none"
                >
                  <Star
                    className={`h-10 w-10 transition-colors ${
                      star <= rating
                        ? 'text-gold-500 fill-current'
                        : 'text-gray-300'
                    }`}
                  />
                </button>
              ))}
            </div>

            {/* Comment */}
            <textarea
              value={ratingComment}
              onChange={(e) => setRatingComment(e.target.value)}
              placeholder="Add a comment (optional)"
              className="input-field w-full mb-4"
              rows={3}
            />

            {/* Actions */}
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowRatingModal(false);
                  setRating(0);
                  setRatingComment('');
                }}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={request.status === 'matched' ? handleCompleteAndRate : handleSubmitRating}
                disabled={rating === 0 || submittingRating}
                className="btn-primary flex-1"
              >
                {submittingRating ? 'Submitting...' : (request.status === 'matched' ? 'Complete & Rate' : 'Submit Rating')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* More Info Modal for Helper Profile */}
      {showMoreInfo && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-apple-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
            style={{ boxShadow: '0 8px 40px rgba(0,0,0,0.16)' }}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-white rounded-t-apple-xl px-6 py-4 flex items-center justify-between"
              style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}
            >
              <h2 className="text-lg font-semibold text-navy-900">Helper Profile</h2>
              <button
                onClick={() => { setShowMoreInfo(null); setMoreInfoData(null); }}
                className="p-1.5 text-navy-400 hover:text-navy-700 hover:bg-navy-100 rounded-full transition-colors duration-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {loadingMoreInfo ? (
              <div className="flex items-center justify-center py-16">
                <div className="spinner w-8 h-8" />
              </div>
            ) : moreInfoData ? (
              <div className="px-6 py-5">
                {/* Header: Photo + Name + Rating */}
                <div className="flex items-center mb-5">
                  <div className="h-16 w-16 bg-navy-100 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
                    {moreInfoData.profile_photo_url ? (
                      <img src={moreInfoData.profile_photo_url} alt="" className="h-16 w-16 rounded-full object-cover" />
                    ) : (
                      <span className="text-navy-600 font-medium text-xl">
                        {moreInfoData.first_name?.[0]}{moreInfoData.last_name?.[0]}
                      </span>
                    )}
                  </div>
                  <div className="ml-4">
                    <p className="text-lg font-medium text-navy-900">
                      {moreInfoData.first_name} {moreInfoData.last_name}
                    </p>
                    <div className="flex items-center text-sm text-navy-500 mt-0.5">
                      {moreInfoData.helper_profile?.average_rating > 0 ? (
                        <>
                          <Star className="h-3.5 w-3.5 text-gold-500 fill-current mr-1" />
                          <span className="font-medium text-navy-700">{moreInfoData.helper_profile.average_rating.toFixed(1)}</span>
                          <span className="text-navy-400 ml-1">({moreInfoData.helper_profile.rating_count} review{moreInfoData.helper_profile.rating_count !== 1 ? 's' : ''})</span>
                        </>
                      ) : (
                        <span className="text-navy-400">No ratings yet</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Bio */}
                {moreInfoData.helper_profile?.bio && (
                  <div className="bg-navy-50 rounded-apple p-4 mb-5">
                    <p className="text-sm text-navy-700 leading-relaxed">{moreInfoData.helper_profile.bio}</p>
                  </div>
                )}

                {/* Stats */}
                <div className="grid grid-cols-3 gap-3 mb-5">
                  <div className="bg-navy-50 rounded-apple py-3 px-2 text-center">
                    <p className="text-xl font-semibold text-navy-900">{moreInfoData.helper_profile?.total_jobs || 0}</p>
                    <p className="text-[11px] text-navy-500 uppercase tracking-wider mt-0.5">Jobs</p>
                  </div>
                  <div className="bg-navy-50 rounded-apple py-3 px-2 text-center">
                    <p className="text-xl font-semibold text-navy-900">{moreInfoData.helper_profile?.experience_years || 0}</p>
                    <p className="text-[11px] text-navy-500 uppercase tracking-wider mt-0.5">Yrs Exp</p>
                  </div>
                  <div className="bg-navy-50 rounded-apple py-3 px-2 text-center">
                    <p className="text-xl font-semibold text-navy-900">{moreInfoData.months_active || 0}</p>
                    <p className="text-[11px] text-navy-500 uppercase tracking-wider mt-0.5">Months</p>
                  </div>
                </div>

                {/* Languages */}
                {moreInfoData.helper_profile?.languages && moreInfoData.helper_profile.languages.length > 0 && (
                  <div className="mb-5 pt-5" style={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                    <p className="text-[11px] font-medium text-navy-400 uppercase tracking-wider mb-2">Languages</p>
                    <p className="text-sm text-navy-700">{moreInfoData.helper_profile.languages.join(' \u2022 ')}</p>
                  </div>
                )}

                {/* Skills */}
                {moreInfoData.helper_profile?.skills && moreInfoData.helper_profile.skills.length > 0 && (
                  <div className="mb-5 pt-5" style={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                    <p className="text-[11px] font-medium text-navy-400 uppercase tracking-wider mb-2">Skills</p>
                    <div className="flex flex-wrap gap-1.5">
                      {moreInfoData.helper_profile.skills.map((skill, idx) => (
                        <span key={idx} className="px-3 py-1 bg-navy-100 text-navy-700 text-xs rounded-full">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Certifications */}
                {moreInfoData.helper_profile?.certifications && (
                  <div className="mb-5 pt-5" style={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                    <p className="text-[11px] font-medium text-navy-400 uppercase tracking-wider mb-2">Certifications</p>
                    <div className="flex items-center text-sm text-navy-700">
                      <Award className="h-4 w-4 mr-1.5 text-primary-600 flex-shrink-0" />
                      {moreInfoData.helper_profile.certifications}
                    </div>
                  </div>
                )}

                {/* Reviews */}
                {moreInfoData.references && moreInfoData.references.length > 0 && (
                  <div className="mb-5 pt-5" style={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                    <p className="text-[11px] font-medium text-navy-400 uppercase tracking-wider mb-3">Past Job Reviews</p>
                    <div className="space-y-2">
                      {moreInfoData.references.map((ref, idx) => (
                        <div key={idx} className="bg-navy-50 rounded-apple p-3.5">
                          <div className="flex items-center justify-between mb-1.5">
                            <div>
                              <span className="text-sm font-medium text-navy-800">{ref.from_name}</span>
                              {ref.yacht_name && (
                                <span className="text-xs text-navy-500 ml-2">on {ref.yacht_name}</span>
                              )}
                            </div>
                            <div className="flex items-center">
                              <Star className="h-3 w-3 text-gold-500 fill-current" />
                              <span className="ml-1 text-sm font-medium text-navy-700">{ref.rating}</span>
                            </div>
                          </div>
                          <p className="text-sm text-navy-600 italic leading-relaxed">"{ref.comment}"</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Member Since */}
                <div className="text-center text-xs text-navy-400 pt-4" style={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                  Member since {format(new Date(moreInfoData.member_since || moreInfoData.created_at), 'MMMM yyyy')}
                </div>
              </div>
            ) : (
              <p className="text-center text-navy-400 py-12">Failed to load profile</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default RequestDetails;
